'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import styles from './MeshFlow.module.css';

/* Client-side only: it mounts a WebGL context, and next/dynamic keeps the
   shader runtime (~77KB gzipped) out of the server bundle and off the initial
   page chunk. */
const MeshGradient = dynamic(
  () => import('@paper-design/shaders-react').then((m) => m.MeshGradient),
  { ssr: false },
);

/* The studies hero SVG's own stops, with the deep two repeated so four of the
   five mesh spots sit at the dark end. The SVG's flat fill #4361A1 is
   deliberately absent: it sat mid-ladder and closed the gap either side of it,
   and the spots merged into one colour. */
const HUB_COLORS = ['#6DA5DC', '#1F387F', '#0B1435', '#1F387F', '#0B1435'];

const SPEED = 0.6;
const SCALE = 1.35;
const DISTORTION = 0.8;
const SWIRL = 0.1;

/* Mount the shader once the page is idle, and not at all under reduced motion
   or data-saver. The layer underneath (the hero SVG on /science/studies, the
   dark card on /science/explainers) is the first paint either way, so LCP
   never waits on the shader chunk. */
const useShaderReady = (eager = false) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const { connection } = navigator as Navigator & {
      connection?: { saveData?: boolean };
    };
    if (connection?.saveData) return undefined;
    // hover-reveal shaders (SoftShader) mount on demand and must appear at once —
    // the up-to-2s idle wait makes a freshly-hovered card look like it has no
    // shader. `eager` mounts the WebGL layer on the next frame instead.
    if (eager) {
      setReady(true);
      return undefined;
    }
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => setReady(true), {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(() => setReady(true), 1);
    return () => clearTimeout(timer);
  }, []);
  return ready;
};

type MeshFlowProps = {
  colors?: string[];
  /* override the module defaults — the progress-bar fill wants a much slower,
     calmer flow than the hero, so it can pass its own speed/scale/swirl. */
  speed?: number;
  scale?: number;
  distortion?: number;
  swirl?: number;
  className?: string;
  /** mount the WebGL layer immediately instead of waiting for idle (hover shaders) */
  eager?: boolean;
};

const MeshFlow = ({
  colors = HUB_COLORS,
  speed = SPEED,
  scale = SCALE,
  distortion = DISTORTION,
  swirl = SWIRL,
  className,
  eager = false,
}: MeshFlowProps) => {
  const ready = useShaderReady(eager);
  return (
    <div className={className ? `${styles.wrap} ${className}` : styles.wrap} aria-hidden="true">
      {ready ? (
        <MeshGradient
          colors={colors}
          speed={speed}
          scale={scale}
          distortion={distortion}
          swirl={swirl}
          width="100%"
          height="100%"
        />
      ) : null}
    </div>
  );
};

export default MeshFlow;
